import React, { useEffect, useRef, useMemo, useState } from "react";
import { View, StyleSheet, Animated, Easing, Platform, LayoutChangeEvent } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";

interface HeaderProgressProps {
  current: number;
  pending: number;
  needed: number;
  preferred: number;
}

export function HeaderProgress({ current, pending, needed, preferred }: HeaderProgressProps) {
  const { colors } = useTheme();
  const [barWidth, setBarWidth] = useState(0);
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  // Projected total after adding pending
  const projected = current + pending;

  // Calculate max for percentage calculations (at least preferred, or projected if higher)
  const max = Math.max(preferred, needed, projected, 1);

  // Progress percentages
  const currentPercent = Math.min((current / max) * 100, 100);
  const pendingPercent = Math.min((pending / max) * 100, 100 - currentPercent);
  const neededPercent = (needed / max) * 100;
  const preferredPercent = (preferred / max) * 100;

  // Determine fill color based on PROJECTED progress (current + pending)
  const colorPalette = useMemo(() => {
    if (projected === 0) return colors.attention; // red
    if (projected < needed) return colors.pending; // amber
    if (projected < preferred) return colors.todo; // blue
    return colors.complete; // green
  }, [projected, needed, preferred, colors]);

  const fillColor = colorPalette.border;

  // Shimmer animation
  useEffect(() => {
    if (pending === 0) return;

    const runShimmer = () => {
      shimmerAnim.setValue(0);
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1200,
        easing: Easing.inOut(Easing.linear),
        useNativeDriver: Platform.OS !== "web",
      }).start(() => runShimmer());
    };
    runShimmer();

    return () => shimmerAnim.stopAnimation();
  }, [pending, shimmerAnim]);

  const onLayout = (e: LayoutChangeEvent) => {
    setBarWidth(e.nativeEvent.layout.width);
  };

  // Shimmer gradient colors
  const shimmerColors = useMemo(() => {
    return [toAlpha(fillColor, 0.3), toAlpha(fillColor, 0.6), toAlpha(fillColor, 0.3)] as [string, string, string];
  }, [fillColor]);

  // Shimmer translation
  const pendingWidth = (pendingPercent / 100) * barWidth;
  const shimmerTranslate = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-50, pendingWidth + 50],
  });

  // Text annotation
  const annotationText = useMemo(() => {
    if (pending > 0) {
      return `${current}+${pending}/${preferred}`;
    }
    return `${current}/${preferred}`;
  }, [current, pending, preferred]);

  return (
    <View style={styles.container}>
      <View style={styles.barContainer} onLayout={onLayout}>
        {/* Background track */}
        <View style={[styles.track, { backgroundColor: colors.backgroundSecondary }]}>
          {/* Current progress (solid) */}
          {currentPercent > 0 && (
            <View
              style={[
                styles.fill,
                {
                  width: `${currentPercent}%`,
                  backgroundColor: fillColor,
                },
              ]}
            />
          )}

          {/* Pending progress (shimmer) */}
          {pendingPercent > 0 && (
            <View
              style={[
                styles.pendingContainer,
                {
                  left: `${currentPercent}%`,
                  width: `${pendingPercent}%`,
                },
              ]}
            >
              <View style={[styles.pendingBase, { backgroundColor: toAlpha(fillColor, 0.25) }]} />
              <Animated.View
                style={[StyleSheet.absoluteFill, { transform: [{ translateX: shimmerTranslate }] }]}
                pointerEvents="none"
              >
                <LinearGradient
                  colors={shimmerColors}
                  start={{ x: 0, y: 0.5 }}
                  end={{ x: 1, y: 0.5 }}
                  style={{ width: 50, height: "100%" }}
                />
              </Animated.View>
            </View>
          )}

          {/* Tick marks */}
          {neededPercent < 100 && (
            <View
              style={[
                styles.tick,
                {
                  left: `${neededPercent}%`,
                  backgroundColor: colors.text,
                },
              ]}
            />
          )}
          {preferredPercent < 100 && preferredPercent !== neededPercent && (
            <View
              style={[
                styles.tick,
                {
                  left: `${preferredPercent}%`,
                  backgroundColor: colors.text,
                },
              ]}
            />
          )}
        </View>
      </View>
      <ThemedText style={[styles.annotation, { color: fillColor }]}>{annotationText}</ThemedText>
    </View>
  );
}

function toAlpha(c: string, a: number): string {
  if (c.startsWith("#")) {
    const hex = c.length === 4 ? `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}` : c;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  if (c.startsWith("rgb")) {
    return c.replace("rgb", "rgba").replace(")", `,${a})`);
  }
  return `rgba(255,255,255,${a})`;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  barContainer: {
    width: 140,
    height: 8,
  },
  track: {
    flex: 1,
    borderRadius: 4,
    overflow: "hidden",
    position: "relative",
  },
  fill: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 4,
  },
  pendingContainer: {
    position: "absolute",
    top: 0,
    bottom: 0,
    overflow: "hidden",
  },
  pendingBase: {
    ...StyleSheet.absoluteFillObject,
  },
  tick: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 2,
    marginLeft: -1,
    opacity: 0.5,
  },
  annotation: {
    fontSize: 12,
    fontWeight: "600",
  },
});
